/**
 * Bookkeeper Assistant — request schemas + wire types shared by the Hono backend
 * (@releve/api) and the web hooks (@releve/web). See docs/BOOKKEEPER_ASSISTANT.md.
 *
 * Org id is taken from the `:orgId` URL path param (requireOrgMember reads it),
 * so it is NOT part of these body schemas.
 */
import { z } from "zod";
/** One turn the client sends: just the new user message. */
export const bookkeeperChatRequestSchema = z.object({
    /** Existing thread to append to; omitted/undefined starts a new thread. */
    threadId: z.string().uuid().optional(),
    /** The user's message. Capped to keep prompt size bounded. */
    message: z.string().min(1).max(4000),
    /** Org base path (e.g. "/org/{id}") so the assistant can build correct links. */
    orgBasePath: z.string().min(1).max(200),
    /** Current app route, for context (e.g. "/org/{id}/accounting/tax"). */
    currentPath: z.string().max(400).optional(),
});
/** PATCH body for renaming a thread. */
export const bookkeeperRenameThreadSchema = z.object({
    title: z.string().min(1).max(120),
});
// ---------- model registry (org + global model config) ----------
/**
 * An OpenRouter `provider/model` slug (e.g. "anthropic/claude-sonnet-5.5"),
 * optionally with a `:variant` suffix on the model (e.g. "deepseek/deepseek-r1:free",
 * "anthropic/claude-3.7-sonnet:thinking"). Lower-case letters, digits, and `._-`,
 * exactly one `/` separating provider from model, and an optional `:` inside the
 * model part. Keeps arbitrary/garbage slugs out at the wire.
 */
export const bookkeeperModelSlugSchema = z
    .string()
    .min(3)
    .max(120)
    .regex(/^[a-z0-9]([a-z0-9._-]*[a-z0-9])?\/[a-z0-9]([a-z0-9._:-]*[a-z0-9])?$/, "Must be a provider/model slug, e.g. anthropic/claude-sonnet-5.5");
/**
 * Super-admin upsert of a registry row (POST /bookkeeper/models). `slug` is the
 * primary key: an existing slug updates, a new one inserts. Setting
 * `is_default: true` is enough — the DB trigger clears the prior default.
 */
export const bookkeeperModelUpsertSchema = z.object({
    slug: bookkeeperModelSlugSchema,
    label: z.string().min(1).max(80),
    provider: z.string().min(1).max(40).optional(),
    enabled: z.boolean().optional(),
    is_default: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(10000).optional(),
});
/**
 * Per-org override write (PATCH /bookkeeper/orgs/:orgId/model). `null` clears the
 * override so the org inherits the global default.
 */
export const bookkeeperOrgModelSchema = z.object({
    slug: bookkeeperModelSlugSchema.nullable(),
});
// ---------- chart spec (SCRUM-174) ----------
/**
 * A chart the assistant attaches to an answer. The model emits this via the
 * `render_chart` presentation tool using numbers it already fetched from the
 * read-only data tools (never invented). The backend validates it against this
 * schema before persisting to `bookkeeper_messages.chart_spec`; the frontend
 * renders it with the shared Highcharts wrapper (`@/components/ui/highcharts`)
 * using Relevé design tokens. Bounds keep payload + render size sane.
 *
 * Shape by type:
 *   - line | column | bar : `categories` are the x-axis labels; each `series`
 *     supplies one `data` value per category (same length/order).
 *   - pie                 : `categories` are the slice labels; exactly one
 *     `series`, whose `data` are the slice values (same length/order).
 */
export const chartSpecSchema = z
    .object({
    type: z.enum(["line", "column", "bar", "pie"]),
    title: z.string().min(1).max(120).optional(),
    /** x-axis / slice labels. */
    categories: z.array(z.string().max(60)).min(1).max(60),
    /** One or more data series (a single series for pie). */
    series: z
        .array(z.object({
        name: z.string().min(1).max(60),
        data: z.array(z.number()).min(1).max(60),
    }))
        .min(1)
        .max(6),
    /** Optional y-axis label, e.g. "kg CO₂e". */
    yAxisLabel: z.string().max(40).optional(),
    /** Optional value suffix for tooltips/labels, e.g. " €" or " kg CO₂e". */
    valueSuffix: z.string().max(16).optional(),
})
    // Cross-field invariants: every series must line up 1:1 with the categories,
    // and a pie has exactly one series. A mismatched spec is discarded (safeParse
    // fails) and the answer degrades to text rather than drawing a misaligned chart.
    .superRefine((spec, ctx) => {
    if (spec.type === "pie" && spec.series.length !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "A pie chart must have exactly one series.",
            path: ["series"],
        });
    }
    spec.series.forEach((s, i) => {
        if (s.data.length !== spec.categories.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `series[${i}].data must have one value per category (${spec.categories.length}).`,
                path: ["series", i, "data"],
            });
        }
    });
});
//# sourceMappingURL=bookkeeper.js.map